import * as React from "react";

import { ViewportSyncMode } from "../messaging";
import { usePlotPanelContext } from "../panelContext/usePlotPanelContext";
import { PlotManager } from "../PlotManager";
import { useViewport } from "../Viewport/useViewport";
import { Viewport } from "../Viewport/viewport";

export function useBroadcastLocalViewport(localViewport: Viewport) {
  // Track the previous viewport to identify when a change has occurred.
  const prevLocalViewport = React.useRef(localViewport);
  const { viewportSyncMode } = usePlotPanelContext();
  const [sharedViewport, setSharedViewport] = useViewport();
  const isInitializedRef = React.useRef(false);

  React.useEffect(
    function onLocalViewportChanged() {
      if (prevLocalViewport.current.isOlderThan(localViewport)) {
        if (!isInitializedRef.current) {
          // If the panel is newly mounted, re-broadcast the shared viewport instead
          // of this panel's viewport.
          // The new panel should focus on the same shared viewport as everything else.
          setSharedViewport(
            new Viewport({
              ...sharedViewport.getParams(),
              debounceId: localViewport.getParams().debounceId,
            }),
          );
          isInitializedRef.current = true;
        } else if (viewportSyncMode) {
          setSharedViewport(
            Viewport.combine(sharedViewport, localViewport, viewportSyncMode),
          );
        }
      }
      prevLocalViewport.current = localViewport;
    },
    [localViewport, viewportSyncMode, sharedViewport, setSharedViewport],
  );
}

export function useOnSyncEnabled(localViewport: Viewport) {
  const { viewportSyncMode } = usePlotPanelContext();
  const [sharedViewport, setSharedViewport] = useViewport();
  // Track the previous viewport to identify when a change has occurred.
  const prevSyncMode = React.useRef<ViewportSyncMode>();
  // Don't synchronize when the component is first mounted.
  const isInitializedRef = React.useRef(false);

  // When this chart starts synchronizing its viewport, sync all other charts' viewports to this one.
  React.useEffect(
    function onViewportSyncEnabled() {
      if (
        viewportSyncMode &&
        viewportSyncMode !== prevSyncMode.current &&
        isInitializedRef.current
      ) {
        setSharedViewport(
          // Because the viewport was just set to synchronize, broadcast
          // this panel's current viewport as the latest viewport.
          Viewport.combine(
            sharedViewport,
            localViewport,
            viewportSyncMode,
            Viewport.latestDebounceId(),
          ),
        );
      }
      isInitializedRef.current = true;
      prevSyncMode.current = viewportSyncMode;
    },
    [localViewport, sharedViewport, viewportSyncMode, setSharedViewport],
  );
}

export function useApplySharedViewport(
  plotManager: PlotManager | null,
  isDragging: boolean,
) {
  const { viewportSyncMode } = usePlotPanelContext();
  const [sharedViewport] = useViewport();
  React.useEffect(
    function applySharedViewport() {
      if (!viewportSyncMode || isDragging) {
        return;
      }
      plotManager?.applyViewport(sharedViewport, viewportSyncMode);
    },
    [sharedViewport, viewportSyncMode, plotManager, isDragging],
  );
}
